import {
  authenticatedFastApiAxiosInstance,
  authenticatedFastApiAxiosInstanceAllowEmptyString,
} from "@/lib/data/http";
import { baseFastApiMatterRoute } from "@/lib/data/requests/matters/matter";
import {
  type APIResponse,
  type MatterTaskWithComments,
  type MatterUserConnection,
} from "@/lib/types";
import { type MatterTask } from "../../../types/Matter";

export type AnnotationParentInput =
  | { matterId: string; massTortId?: never }
  | { matterId?: never; massTortId: string };

export type MatterTaskAssignmentRole = "Assignee" | "Assigner";

export type MatterTaskAssignmentInput = {
  role: MatterTaskAssignmentRole;
  taskIds: string[];
  userId?: string;
  integrationAppId?: string;
};

export type MatterTaskAssignment = MatterTaskAssignmentInput & {
  changedTaskIds: string[];
  unchangedTaskIds: string[];
};

export type DashboardTaskUser = {
  dateCreated: string;
  dateModified: string;
  email: string | null;
  firmId: string;
  firstName: string | null;
  id: string;
  label: string;
  lastName: string | null;
  matterId: string;
  matterTitle: string | null;
  middleName: string | null;
  owner: boolean | null;
  phoneNumber: string | null;
  status: string | null;
  task: MatterTask & {
    userConnections?: MatterUserConnection[];
  };
  taskId: string;
  userId: string;
  username: string | null;
};

type MatterTaskLinkFields = Pick<
  MatterTask,
  | "phaseActionId"
  | "fileId"
  | "vehicleId"
  | "insuranceId"
  | "incidentReportId"
  | "medicalTreatmentRecordId"
  | "medicalTreatmentPlanId"
  | "medicalTreatmentBalanceConfirmationId"
  | "settlementDemandId"
  | "settlementOfferId"
  | "settlementCounterId"
  | "expenseId"
  | "lostWagesId"
  | "lienId"
  | "propertyDamageId"
  | "signatureRequestId"
  | "trustCheckId"
  | "litigationDateId"
  | "statuteOfLimitationsId"
  | "emailId"
> & {
  medicalTreatmentId?: string;
  medicalTreatmentAppointmentId?: string;
  medicalTreatmentBillId?: string;
};

type CreateMatterTaskInput = Pick<MatterTask, "title" | "status"> &
  AnnotationParentInput &
  Partial<
    Pick<
      MatterTask,
      "completedDate" | "description" | "dueDate" | "priority" | "startDate"
    >
  > &
  Partial<MatterTaskLinkFields> & {
    userConnections?: Array<{
      label: string;
      userId?: string;
      integrationAppId?: string;
    }>;
  };

const isBlankDate = (value: string | null | undefined) =>
  typeof value === "string" && value.trim() === "";

const serializeCreateMatterTaskPayload = (data: CreateMatterTaskInput) => {
  const { completedDate, dueDate, startDate, ...rest } = data;

  return {
    ...rest,
    ...(completedDate && !isBlankDate(completedDate) ? { completedDate } : {}),
    ...(dueDate && !isBlankDate(dueDate) ? { dueDate } : {}),
    ...(startDate && !isBlankDate(startDate) ? { startDate } : {}),
  };
};

export const createMatterTaskV3 = (data: CreateMatterTaskInput) => {
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .post<
      APIResponse<MatterTask>
    >("matters/v3/tasks", serializeCreateMatterTaskPayload(data))
    .then((res) => res.data);
};

type UpdateMatterTaskInput = Pick<MatterTask, "id" | "dateModified"> &
  AnnotationParentInput &
  Partial<MatterTask> & { userId?: string };

const serializeUpdateMatterTaskPayload = (data: UpdateMatterTaskInput) => {
  const { id, ...rest } = data;
  return {
    ...rest,
    completedDate: isBlankDate(data.completedDate) ? null : data.completedDate,
    dueDate: isBlankDate(data.dueDate) ? null : data.dueDate,
    id,
    startDate: isBlankDate(data.startDate) ? null : data.startDate,
  };
};

export const updateMatterTaskV1 = (data: UpdateMatterTaskInput) => {
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .patch<
      APIResponse<MatterTask>
    >(`${baseFastApiMatterRoute}/tasks`, serializeUpdateMatterTaskPayload(data))
    .then((res) => res.data);
};

export const updateMatterTasksV1 = (data: UpdateMatterTaskInput[]) => {
  const body = { tasks: data.map(serializeUpdateMatterTaskPayload) };

  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .patch<APIResponse<MatterTask[]>>(`${baseFastApiMatterRoute}/tasks`, body)
    .then((res) => res.data);
};

export const addMatterTaskUserAssignmentsV2 = (
  data: MatterTaskAssignmentInput,
) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterTaskAssignment>
    >(`${baseFastApiMatterRoute}/tasks/assignments`, data)
    .then((res) => res.data);

export const removeMatterTaskUserAssignmentsV2 = (
  data: MatterTaskAssignmentInput,
) =>
  authenticatedFastApiAxiosInstance
    .delete<
      APIResponse<MatterTaskAssignment>
    >(`${baseFastApiMatterRoute}/tasks/assignments`, { data })
    .then((res) => res.data);

export const deleteMatterTaskV1 = (
  data: Pick<MatterTask, "id"> & AnnotationParentInput,
) => {
  const { id } = data;
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .delete<APIResponse<MatterTask>>(`${baseFastApiMatterRoute}/tasks/${id}`)
    .then((res) => res.data);
};

export const deleteMatterTasksV1 = (
  data: (Pick<MatterTask, "id"> & AnnotationParentInput)[],
) => {
  const body = {
    tasks: data.map(({ id, ...task }) => ({
      ...task,
      id,
    })),
  };

  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .delete<
      APIResponse<MatterTask[]>
    >(`${baseFastApiMatterRoute}/tasks/bulk-delete`, { data: body })
    .then((res) => res.data);
};

export type UserTaskStatus = "Open" | "In Progress" | "Completed";
export type UserTaskPriority = "Low" | "Medium" | "High" | "Urgent";
export type UserTaskTimeframe =
  | "overdue"
  | "today"
  | "thisWeek"
  | "nextWeek"
  | "next30Days";

export type RetrieveUserTasksParams = {
  userId?: string;
  pageNumber?: number;
  pageSize?: number;
  status?: UserTaskStatus;
  assigneeUserId?: string[];
  assigneeIntegrationAppId?: string[];
  assignerUserId?: string[];
  priority?: UserTaskPriority[];
  priorityUnassigned?: boolean;
  unassigned?: boolean;
  dueDateFrom?: string;
  dueDateTo?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  timeframe?: UserTaskTimeframe[];
  search?: string;
  sort?: "dateCreated" | "priority" | "dueDate";
  order?: "asc" | "desc";
};

export const retrieveUserTasksV2 = (params: RetrieveUserTasksParams = {}) => {
  return authenticatedFastApiAxiosInstance
    .get<APIResponse<DashboardTaskUser[]>>(
      `${baseFastApiMatterRoute}/tasks/users`,
      {
        params,
        paramsSerializer: { indexes: null },
      },
    )
    .then((res) => res.data);
};

export type RetrieveMatterTasksParams = {
  matterId: string;
  phaseActionId?: string;
  taskId?: string;
  pageNumber?: number;
  pageSize?: number;
  assigneeUserId?: string[];
  assigneeIntegrationAppId?: string[];
  assignerUserId?: string[];
  createdDateFrom?: string;
  createdDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  order?: "asc" | "desc";
  priority?: Array<"Low" | "Medium" | "High" | "Urgent">;
  priorityUnassigned?: boolean;
  search?: string;
  sort?: "dateCreated" | "dueDate" | "priority";
  status?: MatterTask["status"];
  timeframe?: Array<
    "overdue" | "today" | "thisWeek" | "nextWeek" | "next30Days"
  >;
  unassigned?: boolean;
};

const serializeMatterTaskParams = (params: Record<string, unknown>) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    const values = Array.isArray(value) ? value : [value];
    values.forEach((item) => {
      searchParams.append(key, String(item));
    });
  });

  return searchParams.toString();
};

export const retrieveMatterTasksV2 = ({
  matterId,
  pageNumber = 1,
  pageSize = 1000,
  ...params
}: RetrieveMatterTasksParams) => {
  return authenticatedFastApiAxiosInstance
    .get<APIResponse<MatterTaskWithComments[]>>(
      `${baseFastApiMatterRoute}/${matterId}/tasks`,
      {
        params: {
          ...params,
          pageNumber,
          pageSize,
        },
        paramsSerializer: serializeMatterTaskParams,
      },
    )
    .then((res) => res.data);
};

export type AssignmentApp = {
  integrationAppId: string;
  integrationAppName: string | null;
};

export const retrieveAssignmentApps = () =>
  authenticatedFastApiAxiosInstance
    .get<APIResponse<AssignmentApp[]>>("/matters/v2/assignment-apps")
    .then((response) => response.data.data);
